'use strict';

customerZone.service('SubscriptionService', ['UnleashedClient', 'Session', 'config', 'utils', '$q', '$log', 'errorCode',
    'ProductService', function(api, Session, config, utils, $q, $log, errorCode, ProductService){
        var self = this;

        self.DONOR_TYPES = jim.values(jim.DonorType);

        self.SIM_STATE = {
            UNKNOWN: 'unknown',
            CREATED: 'created',
            DELETED: 'deleted',
            DELIVERED: 'delivered',
            DISABLED: 'disabled',
            ENABLED: 'enabled',
            FROZEN: 'frozen',
            LOCKED: 'locked',
            OWNERLESS: 'on_stock',
            ORDERED: 'ordered',
            PARTIALLY_DELETED: 'partially_deleted',
            PENDING: 'pending',
            PRODUCTIVE: 'productive',
            SUSPENDED: 'suspended'
        };

        /**
         * Retrieve a subscription by id
         */
        self.getSubscription = function(id){
            return api.getSubscription(id);
        };

        /**
         * Cancel the given subscription.
         * @param subscription the subscription to cancel
         * @param password password of the subscription owner
         *
         * @return a Promise that will resolve if the subscription is cancelled.
         * If rejected, check the INVALID_CREDENTIALS errorCode to see if the password was incorrect.
         */
        self.cancelSubscription = function(subscription, password){
            return api.cancelSubscription(subscription.id, password);
        };

        /**
         * Migrate the given subscription to a different product.
         * @param subscription the subscription to cancel
         * @param newProductId ID of the new product to link
         * @param initialTopupId ID of the initial topup product (for migrating to prepaid)
         * @param paymentMethodProvider A provider for a payment method.  This should be a function that, when invoked
         *        with a list of available methods, will return a promise to the selected method.  It will only be
         *        invoked if payment is required.
         * @param redirectUrl the url to redirect to after payment, if needed
         * @param password password of the subscription owner
         *
         * @return a Promise that will resolve if the subscription is migrated.
         * If rejected, check the INVALID_CREDENTIALS errorCode to see if the password was incorrect.
         */
        self.migrateSubscription = function(subscription, newProductId, initialTopupId, paymentMethodProvider,
                                            redirectUrl, password){
            if (!subscription.msisdn) throw new Error('Cannot migrate a subscription without an msisdn: ' +
                subscription.id);
            if (subscription.state !== jim.SubscriberState.enabled)
                throw $q.reject(errorCode.wrap(errorCode.WRONG_STATE));
            return api.createOrder(subscription.id, newProductId, initialTopupId ? [initialTopupId] : [], password)
                .then(function(order){
                    return payOrder(order, paymentMethodProvider, redirectUrl);
                })
                .catch(function(error){
                    if (errorCode.is(error, errorCode.INVALID_GRANT))
                        throw errorCode.wrap(errorCode.INVALID_CREDENTIALS);
                    else throw error;
                });
        };
        /**
         * Order a new subscription+sim
         * @param product subscription product to link the sim to (or its ID)
         * @param topupProduct (optional) topup product the user wants to purchase (or ID, only reqd. for prepaid with
         *        delivery)
         * @param mnpDetails object (optional): an object { msisdn, donorIccid, donorOperator } of details if the user
         *        wants to port his number (porting only takes effect after activation)
         * @param options an array of products to add as options to the subscription
         * @param deliveryDetails an object of the form
         *        { company, firstName, lastName, address { street, number, box, city, postcode}}. If omitted no sim
         *        will be delivered.
         * @param paymentMethodProvider A provider for a payment method.  This should be a function that, when invoked
         *        with a list of available methods, will return a promise to the selected method.  It will only be
         *        invoked if payment is required.
         * @param redirectUrl the url to redirect to after the payment is done.
         * @param password the password to use for ordering (if required)
         * @return a promise for the created order
         */
        self.orderSim = function(product, topupProduct, mnpDetails, options, deliveryDetails, paymentMethodProvider,
                                 redirectUrl, password){

            if (mnpDetails && mnpDetails.donorType === 'business_postpaid')
                throw new Error('Professional postpaid is not supported at the moment');

            $log.debug('Ordering sim for product ' + product + ', with porting from ' +
                JSON.stringify(mnpDetails) + ', delivered to ' + JSON.stringify(deliveryDetails));

            var id = function(productOrId){
                if (typeof productOrId === 'object') return productOrId.id;
                else return productOrId;
            };

            options = options ? options.map(id) : [];

            var productId = id(product);
            var topupProductId = id(topupProduct);

            var productIds = [productId];
            if (topupProductId) productIds.push(topupProductId);

            return api.orderSubscriptionAndSim(productId, topupProductId, options, mnpDetails, deliveryDetails,
                password)
                .then(function(creationResult){
                    var order = {
                        id: creationResult.orderId,
                        productIds: productIds,
                        allowedPaymentMethods: creationResult.allowedPaymentMethods
                    };
                    if (creationResult.allowedPaymentMethods){
                        return payOrder(order, paymentMethodProvider, redirectUrl, password);
                    }
                    else {
                        order.amount = 0;
                        return order;
                    }
                });
        };

        self.payOutstandingOrder = function(order, paymentMethodProvider, redirectUrl){
            return payOrder(order, paymentMethodProvider, redirectUrl);
        };

        /**
         * Buy a topup
         *
         * @param subscriptionId Subscription (id) to buy a topup for
         * @param productId Product (id) of the topup to buy
         * @param paymentMethodProvider A provider for a payment method.  This should be a function that, when invoked
         * with a list of available methods, will return a promise to the selected method.  It will only be invoked if
         * payment is required.
         * @param redirectUrl If payment is required, the url to redirect to after payment.
         * @return A promise that will resolve when the topup order is created.
         */
        self.buyTopup = function(subscriptionId, productId, paymentMethodProvider, redirectUrl){
            return api.createOrder(subscriptionId, productId)
                .then(function(order){
                    return payOrder(order, paymentMethodProvider, redirectUrl);
                });
        };

        /**
         * Pay an order (if required).  If the order is 'not_payable' retry for 10 seconds, then give up.
         * @param order the order to pay for
         * @param paymentMethodProvider A provider for a payment method.  This should be a function that, when invoked
         * with a list of available methods, will return a promise to the selected method.  It will only be invoked if
         * payment is required.
         * @param redirectUrl the url to redirect to if needed.
         * @return a promise to an object containing an order Id and (optionally) a paymentUrl.
         */
        var payOrder = function(order, paymentMethodProvider, redirectUrl){
            if (!order.allowedPaymentMethods)
                return $q.when({id: order.id});

            if (!redirectUrl) {
                $log.debug('Order', order);
                $log.error('Order returned allowedPaymentMethods, but no payment was expected.');
                return $q.when({id: order.id});
            }

            redirectUrl = redirectUrl.replace('{ORDER_ID}', order.id);

            var getOrderUpdate = function(){
                return api.getOrder(order.id);
            };

            var orderIsPastPayStep = function(orderUpdate){
                var state = orderUpdate.state;
                // currently, the only 'step' before payment is created.  In the future we might need to add
                // other steps here.
                if (state === jim.OrderState.created) return false;
                if (state === jim.OrderState.unknown) return false;
                return true;
            };

            return utils.poll(getOrderUpdate, orderIsPastPayStep, 1000, 10000, 'wait for payable order')
                .then(function(orderUpdate){
                    if (orderUpdate.state === jim.OrderState.waiting_for_payment){
                        if (!paymentMethodProvider) return orderUpdate;
                        return paymentMethodProvider(orderUpdate.allowedPaymentMethods)
                            .then(function(method) {
                                return api.payOrder(order.id, redirectUrl, method);
                            })
                            .then(function(paymentDetails){
                                return {
                                    id: order.id,
                                    productIds: orderUpdate.productIds,
                                    amount: orderUpdate.amount,
                                    paymentUrl: paymentDetails.paymentUrl
                                };
                            });
                    }
                    else if ([jim.OrderState.failed, jim.OrderState.cancelled].includes(orderUpdate.state)) {
                        throw errorCode.wrap(errorCode.WRONG_STATE);
                    }
                    return {
                        id: order.id,
                        state: orderUpdate.state,
                        productIds: orderUpdate.productIds,
                        amount: 0
                    };
                });
        };

        /**
         * Buy bundles/options for a subscription
         * @param subscriptionId Subscription the bundles are for
         * @param productId id of the bundle product
         * @param redirectUrl where to go after payment (if required)
         * @param paymentMethodProvider A provider for a payment method.  This should be a function that, when invoked
         * with a list of available methods, will return a promise to the selected method.  It will only be invoked if
         * payment is required.
         * @param password a password for the payment (if required)
         * @return A promise to the created order
         */
        self.buyBundle = function(subscriptionId, productId, redirectUrl, paymentMethodProvider, password) {
            return api.createOrder(subscriptionId, productId, [], password)
                .then(function(order){
                    return payOrder(order, paymentMethodProvider, redirectUrl);
                });
        };

        /**
         * Get the latest info on an order
         */
        self.getOrder = function(orderId){
            return api.getOrder(orderId);
        };

        /**
         * Activate a subscription with a simcard
         * @param subscriptionId, id of the subscription to link to the sim (if undefined is given, the first
         *        subscription from the session is used.)
         * @param iccid ICCID of the sim to activate, or four last digits of the ICCID if the current authenticated
         *        user is the owner
         * @return a promise to the result of the activation, in the following form:
         *  {
         *      state: state of the sim (can be activating or active)
         *      iccid: full iccid of the sim
         *  }
         * @throws {code:ERROR.PAYMENT_METHOD_REQUIRED, subscriptionId} if this subscription cannot be activated
         *         without a payment method
         */
        self.activateSubscription = function(iccid){
            $log.debug('Activation request for ' + iccid);
            if (isActive(iccid, Session.subscribers()))
                return $q.reject(errorCode.wrap(errorCode.ALREADY_ACTIVE));
            return api.activateSubscription(iccid)
                .then(function (orderId) {
                    return orderId;
                });
        };

        var isActive = function(iccid, subscribers){
            if (!subscribers) return false;
            return subscribers.filter(function(subscriber){
                if (subscriber.state === jim.SubscriberState.pending ||
                    subscriber.state === jim.SubscriberState.unknown) return false;
                return subscriber.sim && endsWith(subscriber.sim.iccid, iccid);
            }).length > 0;
        };

        var endsWith = function(value, tail){
            if (!value) return false;
            if (value.length < tail.length) return false;
            return value.substr(value.length - tail.length) === tail;
        };

        /**
         * Get the possible payment methods for a given subscription
         * @return a Promise to an object containing payment methods, of the following form:
         * {
         *  'method_one': {
         *      description: 'Pay using method one',
         *      apply: function(subscriptionId, redirectUrl, sepaOptions){...},
         *      active: true
         *    },
         *  'method_two': {...}
         *  }
         *
         *  The currently selected payment method is marked with the 'active' flag.
         *
         *  The apply function can be invoked with the redirectUrl
         *  to use if redirection is needed (e.g. after redirecting to a payment provider).  SepaOptions can be given
         *  if a sepa mandate needs to be created (they will be ignore otherwise)
         *  It returns a promise containing a paymentUrl if redirection is needed.
         */
        self.getPaymentMethods = function(subscriptionId){
            return self.getMandates()
                .then(function(mandates){

                    mandates.forEach(function(mandate){
                        if (!mandate.subscriptionIds)
                            mandate.subscriptionIds = [];
                    });

                    var MANUAL = 'manual';
                    var currentlySelectedMandate = MANUAL;

                    var setActive = function(key){
                        if (result[currentlySelectedMandate])
                            result[currentlySelectedMandate].active = false;
                        currentlySelectedMandate = key;
                        result[currentlySelectedMandate].active = true;
                    };

                    var unlink = function(mandate) {
                        if (!mandate) return $q.resolve();
                        if (!mandate.subscriptionIds.includes(subscriptionId)) {
                            $log.warn('Unlink called, but subscription id not included');
                            return;
                        }
                        return api.unlinkMandate(subscriptionId, mandate.id)
                            .then(function() {
                                mandate.subscriptionIds = mandate.subscriptionIds
                                    .filter(function(id) { return id !== subscriptionId; });
                                if (mandate.subscriptionIds.length === 0)
                                    return api.removeMandate(mandate.id)
                                        .then(function() {
                                            delete result[mandate.id];
                                        });
                            });
                    };

                    var link = function(mandate) {
                        if (mandate.subscriptionIds.includes(subscriptionId)) {
                            $log.warn('Link called, but subscription already linked');
                            return;
                        }
                        return api.linkMandate(subscriptionId, mandate.id)
                            .then(function() {
                                mandate.subscriptionIds.push(subscriptionId);
                            });
                    };

                    var result = {
                        manual: {
                            description: utils.translate('manual_payment'),
                            apply: function () {
                                if (currentlySelectedMandate === MANUAL)
                                    return $q.when({});
                                else {
                                    return unlink(result[currentlySelectedMandate].mandate)
                                        .then(function(){
                                            setActive(MANUAL);
                                            return {};
                                        });
                                }
                            }
                        },

                        'mollie:__new': {
                            description: utils.translate('mandate_creditcard'),
                            apply: function (redirectUrl) {
                                return api.requestNewMandate(subscriptionId, redirectUrl, 'creditcard');
                            }
                        },

                        'doccle:__new': {
                            description: utils.translate('mandate_sepa'),
                            apply: function (redirectUrl, sepaOptions) {
                                return api.requestNewMandate(subscriptionId, redirectUrl, 'sepa',
                                    sepaOptions);
                            }
                        }
                    };

                    mandates.forEach(function(mandate){
                        if (mandate.subscriptionIds.includes(subscriptionId))
                            currentlySelectedMandate = mandate.id;
                        result[mandate.id] = {
                            description: utils.format('pay_auto_using_{label}', {label: mandate.label}),
                            mandate: mandate,
                            apply: (function(mandate){
                                return function(){
                                    if (currentlySelectedMandate === mandate.id)
                                        return $q.when({});
                                    else
                                        return unlink(result[currentlySelectedMandate].mandate)
                                            .then(function() { return link(mandate); })
                                            .then(function() { setActive(mandate.id); })
                                            .then(function() { return {}; });
                                };
                            }(mandate))
                        };
                    });

                    result[currentlySelectedMandate].active = true;

                    return result;
                });
        };

        /**
         * Set auto topup features for a subscription
         * @param subscriptionId: id of the subscription to set up payment for
         * @param topupOptions: (optional) options for auto-topups
         * @return a promise that will resolve when complete
         */
        self.setAutoTopup = function(subscriptionId, topupOptions){
            //TODO: auto-topup: out of scope for now
            return api.setupPayment(subscriptionId, undefined, undefined, topupOptions);
        };


        /**
         * Retrieve the current payment setup for a subscription.
         * @param subscriptionId
         */
        self.getPaymentSetup = function(subscriptionId) {
            return api.getPaymentSetup(subscriptionId);
        };

        self.getInvoicingSettings = function(subscriptionId) {
            return api.getInvoicingSettings(subscriptionId);
        };

        self.setInvoicingSettings = function(subscriptionId, settings) {
            if (settings.billingPreference === 'digital'){
                // If electronic billing is selected, the invoicing address is of no importance.
                // We don't want to show an error for a missing address, so we simply populate it.
                settings.invoicingAddress = {
                    firstName: Session.profile().firstName,
                    lastName: Session.profile().lastName,
                    company: Session.profile().company,
                    address: Session.profile().address
                };
            }
            return api.setInvoicingSettings(subscriptionId, settings);
        };

        /**
         * Retrieve the payment mandates for the current customer.
         * @return a promise to an array of mandates, each one containing an object with at least
         *         an 'id' and a 'description'
         */
        self.getMandates = function(){
            return api.getMandates()
                .then(function(mandates){
                    return mandates;
                });
        };

        /**
         * @return a promise to a list of products that the given subscription is allowed to migrate towards.
         */
        self.getMigrationOptions = function(subscription){
            if (!subscription || !subscription.product) return $q.when([]);
            var result = [];
            var promises = [];

            if (allowPrepaid())
                promises.push(ProductService.getSubscriptionProducts(jim.ProductType.prepaid_subscription)
                    .then(function(prepaidProducts){
                        result.push.apply(result, prepaidProducts);
                    }));

            if (subscription.product.isPostpaid())
                promises.push(ProductService.getSubscriptionProducts(jim.ProductType.postpaid_subscription)
                    .then(function(prepaidProducts){
                        result.push.apply(result, prepaidProducts);
                    }));

            return $q.all(promises).then(function(){
                return result.filter(notCurrentProduct);
            });

            function allowPrepaid(){
                return Session.profile() && !Session.profile().vat;
            }

            function notCurrentProduct(product){
                return product.id !== subscription.product.id;
            }
        };

        /**
         * Update the alias of a subsciption.  The alias is a human-readable name a customer sets on a sim for
         * convenience reasons.
         * @return Promise that resolves when the alias is updated
         */
        self.updateAlias = function(subscription, alias){
            return api.updateAlias(subscription, alias);
        };
    }]);
